/**
 * OrganizationProfile — organization identity + regional preferences.
 *
 * Wave 3.1: migrated from mockData to the live svdy-platform API.
 *
 * Business rule (enforced here, not just by the backend): **timezone is
 * mandatory**. Attendance calculations depend on the org timezone for
 * every clock-in / schedule / approval. The UI blocks save when timezone
 * is empty, shows a sticky warning banner until set, and nudges the user
 * into edit mode on first load if the backend returned a null timezone.
 *
 * Field mapping (frontend → backend):
 *   companyName       → name             (required by backend)
 *   industry          → industry
 *   companySize       → company_size
 *   companyDescription→ description
 *   phoneNumber       → phone
 *   emailAddress      → email
 *   website           → website
 *   contactName       → contact_name
 *   street            → address           (backend collapses street/address)
 *   city              → city
 *   state             → state
 *   country           → country
 *   timezone          → timezone          (required per business rule)
 *   registrationNumber→ registration_number
 *   taxId             → tax_id
 *   defaultLanguage   → language
 *   dateFormat        → date_format
 *   timeFormat        → time_format
 *   defaultCurrency   → currency
 */

import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useState,
} from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Container,
  FormControl,
  FormControlLabel,
  Grid,
  InputAdornment,
  MenuItem,
  Select,
  Snackbar,
  TextField as MuiTextField,
  TextFieldProps as MuiTextFieldProps,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Cancel,
  CloudUpload as CloudUploadIcon,
  Edit,
  PlaceOutlined,
  ReceiptLongOutlined,
  Save,
  WarningAmberOutlined,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { ApiError, organizationApi } from '../../../api';
import type {
  OrganizationProfile,
  OrganizationProfileUpdate,
} from '../../../api/organization';
import {
  companySizes,
  countries,
  countryLabel,
  currencies,
  dateFormats,
  industries,
  languages,
  timeFormats,
  timezoneOptions,
} from '../../../constants/enums';
import {
  LocationPickerDialog,
  type PickedPlace,
} from '../../../components/location';
import { ConfirmDialog, StickyFormActions } from '../../../components/primitives';
import {
  billedToBlock, billingIdentityFromForm,
} from '../../../components/billing/addressFormat';
import SetupGuide from '../../../components/onboarding/SetupGuide';
import PhoneField from '../../../components/forms/PhoneField';
import { validateEmail, validatePhone, validateUrl, styles } from './helpers';
import { translateApiError } from '../../../i18n/translateApiError';



type FormErrors = Record<string, string>;


// ---------------------------------------------------------------------------
// Lifted subcomponents (HANDOFF_2026-05-25 Bug 11b)
//
// Pre-fix these were defined inside OrganizationProfileComponent. Every
// parent re-render (and there's one per keystroke because `setDraft` is
// called on every keypress) created a NEW component type identity, so
// React unmounted + remounted the inputs on every character — producing
// the "input lag / focus loss / 卡顿" symptom the owner reported.
//
// Fix: hoist to module scope, wrap in React.memo, take explicit props.
// Component identity is now stable across renders → reconciler updates
// in place → focus is preserved → no per-keystroke unmount.
// See svdy-rules / session-discipline §11.
// ---------------------------------------------------------------------------

interface ProfileFieldShared {
  draft: OrganizationProfile | null;
  profile: OrganizationProfile | null;
  errors: FormErrors;
  isEditing: boolean;
  onDraftChange: (name: keyof OrganizationProfile, value: unknown) => void;
}

const ProfileTextField: React.FC<
  MuiTextFieldProps & ProfileFieldShared & {
    name: keyof OrganizationProfile;
    icon?: React.ReactNode;
  }
> = React.memo(
  ({ draft, profile, errors, isEditing, onDraftChange, name, icon, sx, helperText, InputProps, ...rest }) => {
    const value =
      ((draft ?? profile) as Record<string, unknown> | null)?.[name as string] ?? '';
    return (
      <MuiTextField
        {...rest}
        variant={isEditing ? 'outlined' : 'standard'}
        value={value}
        onChange={(e) => onDraftChange(name, e.target.value)}
        error={!!errors[name as string]}
        helperText={errors[name as string] || helperText}
        // MUI indents FormHelperText by 14px in the outlined variant, so the
        // label, the input and the help line under it sat at THREE different left
        // edges. Reset once here rather than per field: there are four helperText
        // sites in this file and they were all doing it (svdy-ui §15 — one
        // alignment axis, and a field is not exempt from it).
        FormHelperTextProps={{ sx: { mx: 0 } }}
        size="small"
        InputProps={{
          readOnly: !isEditing,
          disableUnderline: !isEditing,
          startAdornment: icon ? (
            <InputAdornment position="start">{icon}</InputAdornment>
          ) : undefined,
          ...InputProps,
        }}
        sx={{
          mb: isEditing ? 1.25 : 0.75,
          mt: 0,
          '& .MuiInputBase-input': {
            fontSize: '0.925rem',
            fontWeight: !isEditing ? 700 : 400,
            color: 'text.primary',
          },
          '& .MuiInputLabel-root': {
            fontSize: '0.825rem',
            color: 'text.secondary',
            transform: 'translate(0, -1.5px) scale(0.85)',
            transformOrigin: 'top left',
          },
          '& .MuiInputLabel-shrink': {
            transform: 'translate(0, -1.5px) scale(0.85)',
          },
          '& .MuiOutlinedInput-root': { borderRadius: 1 },
          '& .MuiFormLabel-filled': { fontWeight: !isEditing ? 600 : 400 },
          ...sx,
        }}
        fullWidth
      />
    );
  },
);
ProfileTextField.displayName = 'ProfileTextField';

const ProfileEnumSelect: React.FC<
  ProfileFieldShared & {
    name: keyof OrganizationProfile;
    options: readonly { value: string; label: string }[];
    placeholder?: string;
    required?: boolean;
    /** What this field decides elsewhere. Shown while EDITING, which is when it
     *  can still change the choice; the read view is a compact summary and four
     *  extra lines would break its single alignment axis (svdy-ui §15). The
     *  always-visible summary lives in the section note instead. */
    helperText?: string;
  }
> = React.memo(
  ({ draft, profile, errors, isEditing, onDraftChange, name, options, placeholder, required,
     helperText }) => {
    const raw =
      (((draft ?? profile) as Record<string, unknown> | null)?.[name as string] ?? '') as string;
    const resolvedLabel =
      options.find((o) => o.value === raw)?.label ?? raw;
    const err = errors[name as string];

    if (!isEditing) {
      return (
        <MuiTextField
          variant="standard"
          value={resolvedLabel}
          fullWidth
          size="small"
          InputProps={{ readOnly: true, disableUnderline: true }}
          sx={{
            mb: 0.75,
            '& .MuiInputBase-input': {
              fontSize: '0.925rem',
              fontWeight: 700,
              color: 'text.primary',
            },
          }}
        />
      );
    }

    return (
      <FormControl fullWidth error={!!err} required={required} size="small" sx={{ mb: 1.25 }}>
        <Select
          name={name as string}
          value={raw}
          onChange={(e) => onDraftChange(name, e.target.value)}
          displayEmpty
          sx={{
            '&  .MuiInputBase-input': { fontSize: '0.925rem' },
            '& .MuiOutlinedInput-notchedOutline': { borderRadius: 1 },
          }}
        >
          <MenuItem value="" disabled>
            <em style={{ opacity: 0.6 }}>{placeholder ?? 'Select…'}</em>
          </MenuItem>
          {options.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </Select>
        {err ? (
          <Typography color="error" variant="caption" sx={{ ml: 1.5, mt: 0.5 }}>
            {err}
          </Typography>
        ) : helperText ? (
          <Typography color="text.secondary" variant="caption" sx={{ ml: 1.5, mt: 0.5 }}>
            {helperText}
          </Typography>
        ) : null}
      </FormControl>
    );
  },
);
ProfileEnumSelect.displayName = 'ProfileEnumSelect';

const ProfileEnumAutocomplete: React.FC<
  ProfileFieldShared & {
    name: keyof OrganizationProfile;
    options: readonly { value: string; label: string }[];
    placeholder?: string;
    freeSolo?: boolean;
    required?: boolean;
    helperText?: string;
    resolveLabel?: (v: string) => string;
  }
> = React.memo(
  ({ draft, profile, errors, isEditing, onDraftChange, name, options, placeholder, freeSolo, required,
     resolveLabel, helperText }) => {
    const raw =
      (((draft ?? profile) as Record<string, unknown> | null)?.[name as string] ?? '') as string;
    const shownLabel = resolveLabel
      ? resolveLabel(raw)
      : options.find((o) => o.value === raw)?.label ?? raw;
    const err = errors[name as string];

    if (!isEditing) {
      return (
        <MuiTextField
          variant="standard"
          value={shownLabel}
          fullWidth
          size="small"
          InputProps={{ readOnly: true, disableUnderline: true }}
          sx={{
            mb: 0.75,
            '& .MuiInputBase-input': {
              fontSize: '0.925rem',
              fontWeight: 700,
              color: 'text.primary',
            },
          }}
        />
      );
    }

    const selectedOption =
      options.find((o) => o.value === raw) ??
      (freeSolo && raw ? { value: raw, label: raw } : null);

    return (
      <Autocomplete
        freeSolo={freeSolo}
        size="small"
        options={options as { value: string; label: string }[]}
        value={selectedOption}
        getOptionLabel={(opt) =>
          typeof opt === 'string' ? opt : opt.label ?? ''
        }
        isOptionEqualToValue={(opt, val) =>
          typeof opt === 'string' || typeof val === 'string'
            ? opt === val
            : opt.value === val.value
        }
        onChange={(_event, newVal) => {
          let nextRaw = '';
          if (typeof newVal === 'string') nextRaw = newVal;
          else if (newVal) nextRaw = newVal.value;
          onDraftChange(name, nextRaw);
        }}
        onInputChange={(_event, inputVal, reason) => {
          if (freeSolo && reason === 'input') {
            onDraftChange(name, inputVal);
          }
        }}
        renderInput={(params) => (
          <MuiTextField
            {...params}
            placeholder={placeholder}
            error={!!err}
            helperText={err || helperText}
            required={required}
            size="small"
            sx={{
              mb: 1.25,
              '&  .MuiInputBase-input': { fontSize: '0.925rem' },
              '& .MuiOutlinedInput-root': { borderRadius: 1 },
            }}
          />
        )}
      />
    );
  },
);
ProfileEnumAutocomplete.displayName = 'ProfileEnumAutocomplete';



const OrganizationProfileComponent: React.FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();

  const [profile, setProfile] = useState<OrganizationProfile | null>(null);
  const [draft, setDraft] = useState<OrganizationProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  // Confirmation modal for "cancel edit with unsaved changes". Replaces
  // the native window.confirm() call.
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);
  const [logoLocal, setLogoLocal] = useState<string | null>(null);
  const [errors, setErrors] = useState<FormErrors>({});
  const [toast, setToast] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);


  // -------------------------------------------------------------------------
  // Load
  // -------------------------------------------------------------------------

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    try {
      const data = await organizationApi.profileApi.get();
      setProfile(data);
      setDraft(data);
      setLoadError(null);
      // Nudge into edit mode when timezone is missing. Attendance can't
      // run without it, so we make that the first thing the user sees.
      if (!data.timezone) {
        setIsEditing(true);
      }
    } catch (e) {
      setLoadError(
        e instanceof ApiError ? translateApiError(e, t) : t('org:organizationProfile.toast.loadFailed'),
      );
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  // -------------------------------------------------------------------------
  // Validation
  // -------------------------------------------------------------------------

  const validateForm = useCallback((): boolean => {
    if (!draft) return false;
    const newErrors: FormErrors = {};

    // Backend-required:
    if (!draft.name?.trim()) {
      newErrors.name = t('org:organizationProfile.validation.nameRequired');
    }

    // Business-required (not enforced by backend today, enforced here):
    if (!draft.timezone?.trim()) {
      newErrors.timezone = t('org:organizationProfile.validation.timezoneRequired');
    }

    // Optional-with-format:
    if (draft.email && !validateEmail(draft.email)) {
      newErrors.email = t('org:organizationProfile.validation.emailInvalid');
    }
    if (draft.phone && !validatePhone(draft.phone)) {
      newErrors.phone = t('org:organizationProfile.validation.phoneInvalid');
    }
    if (draft.website && !validateUrl(draft.website)) {
      newErrors.website = t('org:organizationProfile.validation.websiteInvalid');
    }
    // The invoice email had NO validation on either side of the wire, and it is
    // the field where a typo costs the most: the provider sends the invoice
    // there, records a delivery, and the customer never receives the document
    // they need to be reimbursed. `email` beside it — a public contact field
    // where a typo is cosmetic — was the only one being checked.
    if (draft.billing_email && !validateEmail(draft.billing_email)) {
      newErrors.billing_email = t('org:organizationProfile.validation.emailInvalid');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [draft, t]);

  const hasUnsavedChanges = useCallback((): boolean => {
    return JSON.stringify(draft) !== JSON.stringify(profile);
  }, [draft, profile]);

  // -------------------------------------------------------------------------
  // Save — builds a diff-only OrganizationProfileUpdate payload
  // -------------------------------------------------------------------------

  const buildUpdate = (): OrganizationProfileUpdate | null => {
    if (!draft || !profile) return null;
    const keys: (keyof OrganizationProfileUpdate)[] = [
      'name',
      'industry',
      'company_size',
      'description',
      'phone',
      'email',
      'website',
      'contact_name',
      'address',
      'city',
      'state',
      'country',
      'timezone',
      'registration_number',
      'tax_id',
      'language',
      'date_format',
      'time_format',
      'currency',
      'postal_code',
      // Suite / unit (alembic 085). Omitted from this list, the field renders,
      // accepts typing, and never reaches the server.
      'address_line2',
      // The billing override and every field it governs. These were missing for
      // the whole of 2026-08-04, which meant the Billing details card — the point
      // of the reimbursable-invoice work — could not save AT ALL. Worse than
      // silently: with only these edited the diff is empty, so `handleSave` shows
      // "no changes" and drops the input; with anything else edited the save
      // succeeds and looks correct while discarding them.
      //
      // This is the client-side twin of the `_BILLING_RELEVANT` gap fixed in
      // svdy-platform the same day. Both were an allowlist that a new field has
      // to be remembered into, so `organizationProfileFields.test.ts` now fails
      // when a rendered input is absent from this list.
      'billing_address_override',
      'billing_name',
      'billing_address',
      'billing_address_line2',
      'billing_city',
      'billing_state',
      'billing_country',
      'billing_postal_code',
      'billing_email',
    ];
    const diff: OrganizationProfileUpdate = {};
    for (const key of keys) {
      if ((draft as Record<string, unknown>)[key] !==
          (profile as Record<string, unknown>)[key]) {
        (diff as Record<string, unknown>)[key] = (draft as Record<string, unknown>)[key];
      }
    }
    return diff;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    const update = buildUpdate();
    if (!update || Object.keys(update).length === 0) {
      setIsEditing(false);
      setToast(t('org:organizationProfile.toast.noChanges'));
      return;
    }

    setSaving(true);
    try {
      const saved = await organizationApi.profileApi.update(update);
      setProfile(saved);
      setDraft(saved);
      setIsEditing(false);
      setErrors({});
      setToast(t('org:organizationProfile.toast.saved'));
    } catch (e) {
      const message =
        e instanceof ApiError ? translateApiError(e, t) : t('org:organizationProfile.toast.saveFailed');
      setErrors((prev) => ({ ...prev, _global: message }));
    } finally {
      setSaving(false);
    }
  };

  // -------------------------------------------------------------------------
  // Edit / cancel
  // -------------------------------------------------------------------------

  const handleEditToggle = () => {
    if (isEditing) {
      if (hasUnsavedChanges()) {
        // Dialog-driven now — setConfirmingDiscard opens the ConfirmDialog
        // below, which calls handleConfirmDiscard on confirm.
        setConfirmingDiscard(true);
      } else {
        setIsEditing(false);
        setErrors({});
      }
    } else {
      setIsEditing(true);
    }
  };

  const handleConfirmDiscard = () => {
    setDraft(profile);
    setIsEditing(false);
    setErrors({});
  };

  // -------------------------------------------------------------------------
  // Input handlers (draft-local; no network until save)
  // -------------------------------------------------------------------------
  // (handleInputChange / handleSelectChange consolidated into
  //  handleDraftChange below — used by the lifted subcomponents.)
  // -------------------------------------------------------------------------

  const handleLogoChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    // Local preview while we upload — gives instant feedback so the
    // user doesn't think nothing happened.
    const reader = new FileReader();
    reader.onloadend = () => setLogoLocal(reader.result as string);
    reader.readAsDataURL(file);

    // HANDOFF_2026-05-25 Bug 11a — actually persist the logo via the
    // backend endpoint so it survives a refresh. Pre-fix only the
    // local data: URL was kept.
    try {
      const updated = await organizationApi.profileApi.uploadLogo(file);
      setProfile(updated);
      setDraft((prev) =>
        prev ? { ...prev, logo_url: updated.logo_url } : updated,
      );
      setLogoLocal(null);
      setToast(t('org:organizationProfile.toast.logoUploaded'));
    } catch (e) {
      const message =
        e instanceof ApiError ? translateApiError(e, t) : t('org:organizationProfile.toast.logoFailed');
      setErrors((prev) => ({ ...prev, _global: message }));
      setLogoLocal(null);
    }
  };

  // Single change-handler reused by every lifted subcomponent.
  // Both setDraft + clear-on-next-keystroke errors live here so the
  // subcomponents don't have to know about FormErrors plumbing.
  const handleDraftChange = useCallback(
    (name: keyof OrganizationProfile, value: unknown) => {
      setDraft((prev) =>
        prev ? ({ ...prev, [name]: value } as OrganizationProfile) : prev,
      );
      setErrors((prev) =>
        prev[name as string] ? { ...prev, [name as string]: '' } : prev,
      );
    },
    [],
  );

  // LocationPickerDialog → apply the picked address to the draft. Street /
  // city / state / country / postal_code / timezone are all filled in one
  // shot; the user remains free to hand-edit any of them afterwards.
  const handleLocationPick = useCallback((place: PickedPlace) => {
    setDraft((prev) => {
      if (!prev) return prev;
      const next: OrganizationProfile = {
        ...prev,
        address: place.street_address || prev.address,
        city: place.city || prev.city,
        state: place.state || prev.state,
        country: place.country || prev.country,
        postal_code: place.postal_code || prev.postal_code,
      };
      if (place.timezone) {
        next.timezone = place.timezone;
      }
      return next;
    });
    // Moving the user out of error state when timezone was previously missing.
    if (place.timezone && errors.timezone) {
      setErrors((prev) => ({ ...prev, timezone: '' }));
    }
    setToast(
      place.timezone
        ? t('org:organizationProfile.toast.appliedWithTimezone', { name: place.timezone })
        : t('org:organizationProfile.toast.appliedNoTimezone'),
    );
  }, [errors.timezone, t]);

  // Shared props bundle passed to every lifted subcomponent — keeps
  // the JSX call sites tidy.
  const fieldShared = {
    draft,
    profile,
    errors,
    isEditing,
    onDraftChange: handleDraftChange,
  };

  // The invoice "Bill to" block, as lines.
  //
  // This FOLLOWS THE DRAFT, and the earlier version did not. It read the server's
  // saved `billing_preview` while the admin was editing the address right beside
  // it, so the card said "Your invoices will show" above the OLD address the
  // whole time someone was typing a new one. The justification in the comment it
  // replaces — that re-deriving the inheritance rule client-side could break a
  // promise about the invoice — had the tradeoff backwards: a preview that lags
  // is not cautious, it misinforms, and it is the display-does-not-match-submit
  // defect this project already has a rule about.
  //
  // Note what this does NOT do: it does not re-implement the inheritance rule.
  // The override checkbox IS the rule, it lives in the draft, and the form
  // already renders the billing_* inputs only when it is on. So this reads
  // "whichever address fields the form is currently editing" — the form's own
  // state, not a second copy of a backend decision.
  const preview = profile?.billing_preview ?? null;
  const draftOrSaved = draft ?? profile;
  const billingPreviewLines = React.useMemo(() => {
    if (!draftOrSaved) return [];
    return billedToBlock(billingIdentityFromForm(draftOrSaved as never));
  }, [draftOrSaved]);
  // `missing` stays SERVER-resolved so it describes what the INVOICE will say
  // rather than what the form guesses — the inheritance rule (company address vs
  // billing override) lives on the server and a client copy could promise
  // something the invoice contradicts.
  //
  // It is NOT a gate: audited 2026-08-06, `missing_for_reimbursement` has exactly
  // two consumers and both are display — this card and the "Billed to" warning on
  // System → Billing. Nothing 422s and nothing blocks a checkout. That is
  // deliberate: refusing a save or a purchase over an incomplete address would
  // block revenue to fix bookkeeping, and the warning reaches the admin during
  // the trial, before conversion, where it can still be acted on.
  const billingMissing = preview?.missing ?? [];

  // -------------------------------------------------------------------------
  // (subcomponents now live at module scope above OrganizationProfileComponent)
  // -------------------------------------------------------------------------

  // -------------------------------------------------------------------------
  // Render guards
  // -------------------------------------------------------------------------

  if (loading && !profile) {
    return (
      <Container
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '80vh',
        }}
      >
        <CircularProgress />
      </Container>
    );
  }

  if (loadError && !profile) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={fetchProfile}>
              {t('common:actions.retry')}
            </Button>
          }
        >
          {loadError}
        </Alert>
      </Container>
    );
  }

  const timezoneMissing = !draft?.timezone;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        py: '12px',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', md: 'center' },
          mb: 1.5,
          gap: 1.5,
        }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              fontSize: '1.125rem',
              color: 'text.primary',
            }}
          >
            {t('org:organizationProfile.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('org:organizationProfile.subtitle')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {!isEditing && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<Edit />}
              onClick={handleEditToggle}
              sx={{
                borderRadius: 1,
                textTransform: 'none',
                fontSize: '0.875rem',
                border: '1px solid',
                bgcolor: 'background.paper',
                color: 'primary.main',
                borderColor: 'primary.main',
                boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
                '&:hover': {
                  bgcolor: 'rgba(25, 118, 210, 0.04)',
                  borderColor: 'primary.dark',
                  boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
                },
              }}
            >
              {t('org:organizationProfile.editButton')}
            </Button>
          )}
        </Box>
      </Box>

      {/* Timezone warning — sticky until user sets it (business gate, not
          just a form error). Dismiss-state not persisted: this MUST stay
          loud until resolved because attendance depends on it. */}
      {timezoneMissing && (
        <Alert
          severity="warning"
          icon={<WarningAmberOutlined />}
          sx={{ width: '100%', mb: 2, alignItems: 'center' }}
          action={
            !isEditing && (
              <Button
                color="inherit"
                size="small"
                onClick={() => setIsEditing(true)}
              >
                {t('org:organizationProfile.timezoneWarning.setNow')}
              </Button>
            )
          }
        >
          <strong>{t('org:organizationProfile.timezoneWarning.headline')}</strong>{' '}
          {t('org:organizationProfile.timezoneWarning.body')}
        </Alert>
      )}

      {/* Setup Guide — backend-driven (ADR-019 §D8) */}
      <SetupGuide section="organization" />

      {/* Inline save error */}
      {errors._global && (
        <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
          {errors._global}
        </Alert>
      )}

      {/* Cards Grid */}
      <Grid
        container
        spacing={2}
        sx={!isEditing ? styles.noFocusAnimation : {}}
      >
        {/* General Information */}
        <Grid item xs={12}>
          <Card sx={styles.card}>
            <Box sx={styles.cardTitleBar}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 600, fontSize: '0.95rem' }}
              >
                {t('org:organizationProfile.sections.general')}
              </Typography>
            </Box>
            <CardContent sx={styles.cardContent}>
              <Grid container spacing={2} alignItems="center">
                <Grid
                  item
                  xs={12}
                  sm={2}
                  container
                  direction="column"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Box
                    component="label"
                    htmlFor={isEditing ? 'logo-upload-input' : undefined}
                    sx={{
                      width: '100%',
                      aspectRatio: '200 / 75',
                      maxWidth: 160,
                      border: `1px dashed ${theme.palette.divider}`,
                      borderRadius: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: isEditing ? 'pointer' : 'default',
                      position: 'relative',
                      overflow: 'hidden',
                      mb: 0.5,
                      bgcolor:
                        logoLocal || draft?.logo_url
                          ? 'transparent'
                          : theme.palette.grey[50],
                      '&:hover': isEditing
                        ? {
                            borderColor: theme.palette.primary.light,
                            bgcolor: 'rgba(25, 118, 210, 0.04)',
                          }
                        : {},
                    }}
                  >
                    {logoLocal || draft?.logo_url ? (
                      <img
                        src={logoLocal || draft?.logo_url || ''}
                        alt={t('org:organizationProfile.fields.logoCompanyAlt')}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                        }}
                      />
                    ) : (
                      <>
                        <CloudUploadIcon
                          sx={{
                            fontSize: 24,
                            color: 'text.secondary',
                            mb: 0.5,
                          }}
                        />
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          textAlign="center"
                          sx={{ px: 1, fontSize: '0.7rem' }}
                        >
                          {isEditing ? t('org:organizationProfile.fields.logoUpload') : t('org:organizationProfile.fields.logoNone')}
                        </Typography>
                      </>
                    )}
                    {isEditing && (
                      <input
                        id="logo-upload-input"
                        type="file"
                        hidden
                        accept=".jpg, .jpeg, .png, .svg, .webp"
                        onChange={handleLogoChange}
                      />
                    )}
                  </Box>
                  <Typography
                    variant="caption"
                    display="block"
                    color="text.secondary"
                    textAlign="center"
                    sx={{ fontSize: '0.65rem' }}
                  >
                    {t('org:organizationProfile.fields.logoSpec')}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={10}>
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} sm={4}>
                      <Typography sx={styles.label}>{t('org:organizationProfile.fields.name')}</Typography>
                      <ProfileTextField
                        {...fieldShared}
                        name="name"
                        placeholder={t('org:organizationProfile.fields.namePlaceholder')}
                        sx={{
                          '& .MuiInputBase-input': {
                            fontSize: '1.125rem',
                            fontWeight: 600,
                            color: theme.palette.text.primary,
                          },
                        }}
                        InputLabelProps={{ shrink: true }}
                        label=""
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography sx={styles.label}>{t('org:organizationProfile.fields.industry')}</Typography>
                      <ProfileEnumAutocomplete
                        {...fieldShared}
                        name="industry"
                        options={industries}
                        placeholder={t('org:organizationProfile.fields.industryPlaceholder')}
                        freeSolo
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography sx={styles.label}>{t('org:organizationProfile.fields.companySize')}</Typography>
                      <ProfileEnumAutocomplete
                        {...fieldShared}
                        name="company_size"
                        options={companySizes}
                        placeholder={t('org:organizationProfile.fields.companySizePlaceholder')}
                        freeSolo
                      />
                    </Grid>
                  </Grid>
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} sm={4}>
                      <Typography sx={styles.label}>
                        {t('org:organizationProfile.fields.registrationNumber')}
                      </Typography>
                      <ProfileTextField
                        {...fieldShared}
                        name="registration_number"
                        placeholder={t('org:organizationProfile.fields.registrationNumberPlaceholder')}
                        InputLabelProps={{ shrink: true }}
                        label=""
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography sx={styles.label}>
                        {t('org:organizationProfile.fields.taxId')}
                      </Typography>
                      <ProfileTextField
                        {...fieldShared}
                        name="tax_id"
                        placeholder={t('org:organizationProfile.fields.taxIdPlaceholder')}
                        InputLabelProps={{ shrink: true }}
                        label=""
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography sx={styles.label}>{t('org:organizationProfile.fields.website')}</Typography>
                      <ProfileTextField
                        {...fieldShared}
                        name="website"
                        type="url"
                        placeholder={t('org:organizationProfile.fields.websitePlaceholder')}
                        InputLabelProps={{ shrink: true }}
                        label=""
                      />
                    </Grid>
                  </Grid>
                  {/* Contact — merged in from a separate half-width card that
                      wrapped to a row of its own with nothing beside it. The
                      page's grid was full, full, half, half, half; the fifth
                      card had no partner, so it read as orphaned and the row
                      above stretched to the taller card, leaving a void under
                      Headquarters. "Who this company is" and "how to reach
                      them" are one concept, so this is a merge rather than a
                      reshuffle. */}
                  <Grid container spacing={1.5} sx={{ mb: 0 }}>
                    <Grid item xs={12} sm={4}>
                      <Typography sx={styles.label}>{t('org:organizationProfile.fields.contactName')}</Typography>
                      <ProfileTextField
                        {...fieldShared}
                        name="contact_name"
                        placeholder={t('org:organizationProfile.fields.contactNamePlaceholder')}
                        InputLabelProps={{ shrink: true }}
                        label=""
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                  <Typography sx={styles.label}>{t('org:organizationProfile.fields.phone')}</Typography>
                  {isEditing ? (
                    // Wrapped rather than given `sx`: PhoneField is a shared
                    // primitive that takes no style prop, and widening its API for
                    // one caller's margin is the wrong trade. The 1.25 matches what
                    // its row-mates get from ProfileTextField's FormControl — with
                    // margin="none" alone this column sat 10px shorter than the
                    // fields beside it, the same one-rule-per-row problem as the
                    // mb: 0 overrides removed from the two rows above.
                    <Box sx={{ mb: 1.25 }}>
                      <PhoneField
                        label=""
                        value={(draft ?? profile)?.phone ?? ''}
                        onChange={(v) =>
                          setDraft((prev) => prev ? { ...prev, phone: v ?? '' } : prev)
                        }
                        defaultCountry={(draft ?? profile)?.country || 'US'}
                        disabled={false}
                        fullWidth
                        margin="none"
                        size="small"
                      />
                    </Box>
                  ) : (
                    // 2026-05-25 Bug 11d redo — view mode no longer renders
                    // a disabled OutlinedInput around the phone (the
                    // "为什么有个框" complaint). Plain Typography matches
                    // the read-only look of every other ProfileTextField
                    // in this file. Same height contract as the rest of
                    // the row (size="small" → 32px-ish).
                    <Typography
                      sx={{
                        fontSize: '0.925rem',
                        fontWeight: 600,
                        color: 'text.primary',
                        minHeight: 32,
                        display: 'flex',
                        alignItems: 'center',
                        mb: 0.75,
                      }}
                    >
                      {(draft ?? profile)?.phone || '—'}
                    </Typography>
                  )}
                </Grid>
                    <Grid item xs={12} sm={4}>
                  <Typography sx={styles.label}>{t('org:organizationProfile.fields.email')}</Typography>
                  <ProfileTextField
                        {...fieldShared}
                    name="email"
                    type="email"
                    placeholder={t('org:organizationProfile.fields.emailPlaceholder')}
                    InputLabelProps={{ shrink: true }}
                    label=""
                  />
                </Grid>
                  </Grid>
                  {/* Description last and full width: the only multiline field on
                      this card, so a third of a row of single-line inputs was
                      always the wrong shape for it. */}
                  <Grid container spacing={1.5} sx={{ mb: 0 }}>
                    <Grid item xs={12}>
                      <Typography sx={styles.label}>{t('org:organizationProfile.fields.description')}</Typography>
                      <ProfileTextField
                        {...fieldShared}
                        name="description"
                        multiline
                        rows={isEditing ? 2 : 1}
                        placeholder={t('org:organizationProfile.fields.descriptionPlaceholder')}
                        InputLabelProps={{ shrink: true }}
                        label=""
                        sx={{ mb: 0 }}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Regional Settings */}
        <Grid item xs={12}>
          <Card sx={styles.card}>
            <Box sx={styles.cardTitleBar}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 600, fontSize: '0.95rem' }}
              >
                {t('org:organizationProfile.sections.regional')}
                <Typography
                  component="span"
                  sx={{
                    fontSize: '0.72rem',
                    color: 'text.secondary',
                    fontWeight: 400,
                    ml: 1.5,
                  }}
                >
                  {t('org:organizationProfile.sections.regionalNote')}
                </Typography>
              </Typography>
            </Box>
            <CardContent sx={styles.cardContent}>
              {/* One row, five fields. The time zone was xs={12} (the whole card,
                  ~1450px) above a row of four sm={3} fields — a field four times
                  its row-mates for a value that needs ~300px. It gets sm={4}
                  rather than sm={3} because "(GMT+8) Shanghai — Asia/Shanghai" is
                  the longest value on this card and must not ellipsize. */}
              <Grid container spacing={1.5} sx={{ mb: 0 }}>
                <Grid item xs={12} sm={4}>
                  <Typography sx={styles.label}>
                    {t('org:organizationProfile.fields.timezone')}{' '}
                    <Box
                      component="span"
                      sx={{ color: 'error.main', fontWeight: 700 }}
                    >
                      *
                    </Box>
                  </Typography>
                  <ProfileEnumAutocomplete
                        {...fieldShared}
                    name="timezone"
                    options={timezoneOptions}
                    placeholder={t('org:organizationProfile.fields.timezonePlaceholder')}
                    helperText={t('org:organizationProfile.fields.timezoneHelp')}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <Typography sx={styles.label}>{t('org:organizationProfile.fields.language')}</Typography>
                  <ProfileEnumSelect
                        {...fieldShared}
                    name="language"
                    options={languages}
                    placeholder={t('org:organizationProfile.fields.languagePlaceholder')}
                    helperText={t('org:organizationProfile.fields.languageHelp')}
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <Typography sx={styles.label}>{t('org:organizationProfile.fields.dateFormat')}</Typography>
                  <ProfileEnumSelect
                        {...fieldShared}
                    name="date_format"
                    options={dateFormats}
                    placeholder={t('org:organizationProfile.fields.dateFormatPlaceholder')}
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <Typography sx={styles.label}>{t('org:organizationProfile.fields.timeFormat')}</Typography>
                  <ProfileEnumSelect
                        {...fieldShared}
                    name="time_format"
                    options={timeFormats}
                    placeholder={t('org:organizationProfile.fields.timeFormatPlaceholder')}
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <Typography sx={styles.label}>{t('org:organizationProfile.fields.currency')}</Typography>
                  <ProfileEnumSelect
                        {...fieldShared}
                    name="currency"
                    options={currencies}
                    placeholder={t('org:organizationProfile.fields.currencyPlaceholder')}
                    helperText={t('org:organizationProfile.fields.currencyHelp')}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Headquarters — street address + location. Timezone moved to
             Regional Settings where it belongs semantically. */}
        <Grid item xs={12} md={6}>
          <Card sx={styles.card}>
            <Box
              sx={{
                ...styles.cardTitleBar,
                justifyContent: 'space-between',
                // 2026-05-25 Bug 20 — explicit minHeight + center
                // alignment so the Typography baseline and the
                // contained Button's centerline land on the same line.
                // Pre-fix the contained-variant button (taller than the
                // text) shifted the row's effective center, making the
                // button visually 'sit higher' than the Headquarters
                // title.
                minHeight: 40,
                alignItems: 'center',
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  lineHeight: 1,  // collapse to single line so text
                                  // visual center == button center
                  m: 0,
                }}
              >
                {t('org:organizationProfile.sections.headquarters')}
              </Typography>
              {isEditing && (
                <Tooltip
                  title={t('org:organizationProfile.headquarters.autoFillTooltip')}
                  arrow
                  placement="top"
                >
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    startIcon={<PlaceOutlined />}
                    onClick={() => setPickerOpen(true)}
                    sx={{
                      borderRadius: 1,
                      textTransform: 'none',
                      fontSize: '0.8rem',
                      boxShadow: 'none',
                      height: 30,
                      lineHeight: 1,
                    }}
                  >
                    {t('org:organizationProfile.headquarters.autoFillButton')}
                  </Button>
                </Tooltip>
              )}
            </Box>
            <CardContent sx={styles.cardContent}>
              <Typography sx={styles.label}>{t('org:organizationProfile.fields.address')}</Typography>
              <ProfileTextField
                        {...fieldShared}
                name="address"
                placeholder={t('org:organizationProfile.fields.addressPlaceholder')}
                InputLabelProps={{ shrink: true }}
                label=""
              />
              {/* Suite / unit — alembic 085. The address had one line, so the
                  suite Stripe already held ("Suite 305") had nowhere to live
                  here. A US business address without it is an accounts-payable
                  matching problem, which is the failure the reimbursable-invoice
                  work exists to remove. Optional: plenty of tenants occupy a
                  whole building. */}
              <Typography sx={styles.label}>{t('org:organizationProfile.fields.addressLine2')}</Typography>
              <ProfileTextField
                        {...fieldShared}
                name="address_line2"
                placeholder={t('org:organizationProfile.fields.addressLine2Placeholder')}
                InputLabelProps={{ shrink: true }}
                label=""
              />
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}>
                  <Typography sx={styles.label}>{t('org:organizationProfile.fields.city')}</Typography>
                  <ProfileTextField
                        {...fieldShared}
                    name="city"
                    placeholder={t('org:organizationProfile.fields.cityPlaceholder')}
                    InputLabelProps={{ shrink: true }}
                    label=""
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography sx={styles.label}>{t('org:organizationProfile.fields.state')}</Typography>
                  <ProfileTextField
                        {...fieldShared}
                    name="state"
                    placeholder={t('org:organizationProfile.fields.statePlaceholder')}
                    InputLabelProps={{ shrink: true }}
                    label=""
                  />
                </Grid>
              </Grid>
              <Grid container spacing={1.5} sx={{ mb: 0 }}>
                <Grid item xs={12} sm={6}>
                  <Typography sx={styles.label}>{t('org:organizationProfile.fields.country')}</Typography>
                  <ProfileEnumAutocomplete
                        {...fieldShared}
                    name="country"
                    options={countries}
                    placeholder={t('org:organizationProfile.fields.countryPlaceholder')}
                    resolveLabel={countryLabel}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography sx={styles.label}>{t('org:organizationProfile.fields.postalCode')}</Typography>
                  <ProfileTextField
                        {...fieldShared}
                    name="postal_code"
                    placeholder={t('org:organizationProfile.fields.postalCodePlaceholder')}
                    InputLabelProps={{ shrink: true }}
                    label=""
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Billing details — who the INVOICE is addressed to.
            Placed directly after Headquarters because that is what it inherits
            from, and the reading order should mirror the inheritance.

            The preview is rendered from `billing_preview`, which the API resolves.
            It is deliberately NOT recomputed here: a preview that applies the
            inheritance rule itself is a promise the invoice can break, and the
            invoice is the document a finance team files. */}
        <Grid item xs={12} md={6}>
          <Card sx={styles.card}>
            <Box sx={styles.cardTitleBar}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600, fontSize: '0.95rem',
                  display: 'flex', alignItems: 'center', gap: 0.75,
                }}
              >
                <ReceiptLongOutlined sx={{ fontSize: 18, color: 'text.secondary' }} />
                {t('org:organizationProfile.sections.billing')}
              </Typography>
            </Box>
            <CardContent sx={styles.cardContent}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {t('org:organizationProfile.billing.intro')}
              </Typography>

              {/* What the invoice will actually say. Read-only by construction. */}
              <Box
                sx={{
                  border: '1px solid', borderColor: 'divider', borderRadius: 1,
                  p: 1.5, mb: 2, bgcolor: 'action.hover',
                }}
              >
                <Typography variant="caption" color="text.secondary"
                  sx={{ display: 'block', mb: 0.5 }}>
                  {t('org:organizationProfile.billing.previewTitle')}
                </Typography>
                {billingPreviewLines.length > 0 ? (
                  billingPreviewLines.map((line, i) => (
                    <Typography key={i} variant="body2"
                      sx={{ fontWeight: i === 0 ? 600 : 400, lineHeight: 1.5 }}>
                      {line}
                    </Typography>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {t('org:organizationProfile.billing.previewEmpty')}
                  </Typography>
                )}
                {billingMissing.length > 0 && (
                  <Alert severity="warning" sx={{ mt: 1.5, py: 0, fontSize: '0.8125rem' }}>
                    {t('org:organizationProfile.billing.missing', {
                      fields: billingMissing
                        .map((f) => t(`org:organizationProfile.billing.field.${f}`))
                        .join(', '),
                    })}
                  </Alert>
                )}
                {!draft?.tax_id && (
                  <Typography variant="caption" color="text.secondary"
                    sx={{ display: 'block', mt: 1 }}>
                    {t('org:organizationProfile.billing.noTaxId')}
                  </Typography>
                )}
              </Box>

              <FormControlLabel
                sx={{ mb: draft?.billing_address_override ? 1 : 0, alignItems: 'flex-start' }}
                control={
                  <Checkbox
                    size="small"
                    disabled={!isEditing}
                    checked={Boolean(draft?.billing_address_override)}
                    onChange={(e) =>
                      handleDraftChange('billing_address_override', e.target.checked)}
                    sx={{ pt: 0.25 }}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2">
                      {t('org:organizationProfile.billing.overrideLabel')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {draft?.billing_address_override
                        ? t('org:organizationProfile.billing.overrideOnHelp')
                        : t('org:organizationProfile.billing.overrideOffHelp')}
                    </Typography>
                  </Box>
                }
              />

              {draft?.billing_address_override && (
                <>
                  <Typography sx={styles.label}>
                    {t('org:organizationProfile.billing.fields.name')}
                  </Typography>
                  <ProfileTextField
                    {...fieldShared}
                    name="billing_name"
                    placeholder={draft?.name || ''}
                    InputLabelProps={{ shrink: true }}
                    label=""
                  />
                  <Typography sx={styles.label}>
                    {t('org:organizationProfile.fields.address')}
                  </Typography>
                  <ProfileTextField
                    {...fieldShared}
                    name="billing_address"
                    placeholder={t('org:organizationProfile.fields.addressPlaceholder')}
                    InputLabelProps={{ shrink: true }}
                    label=""
                  />
                  {/* The override gets its OWN suite line. Sharing one with the
                      company address would silently drop it for exactly the
                      tenants who bothered to configure billing separately. */}
                  <Typography sx={styles.label}>
                    {t('org:organizationProfile.fields.addressLine2')}
                  </Typography>
                  <ProfileTextField
                    {...fieldShared}
                    name="billing_address_line2"
                    placeholder={t('org:organizationProfile.fields.addressLine2Placeholder')}
                    InputLabelProps={{ shrink: true }}
                    label=""
                  />
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} sm={6}>
                      <Typography sx={styles.label}>
                        {t('org:organizationProfile.fields.city')}
                      </Typography>
                      <ProfileTextField
                        {...fieldShared}
                        name="billing_city"
                        InputLabelProps={{ shrink: true }}
                        label=""
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography sx={styles.label}>
                        {t('org:organizationProfile.fields.state')}
                      </Typography>
                      <ProfileTextField
                        {...fieldShared}
                        name="billing_state"
                        InputLabelProps={{ shrink: true }}
                        label=""
                      />
                    </Grid>
                  </Grid>
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} sm={6}>
                      <Typography sx={styles.label}>
                        {t('org:organizationProfile.fields.country')}
                      </Typography>
                      <ProfileEnumAutocomplete
                        {...fieldShared}
                        name="billing_country"
                        options={countries}
                        placeholder={t('org:organizationProfile.fields.countryPlaceholder')}
                        resolveLabel={countryLabel}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography sx={styles.label}>
                        {t('org:organizationProfile.fields.postalCode')}
                      </Typography>
                      <ProfileTextField
                        {...fieldShared}
                        name="billing_postal_code"
                        InputLabelProps={{ shrink: true }}
                        label=""
                      />
                    </Grid>
                  </Grid>
                </>
              )}

              {/* Redirects the PROVIDER's invoice; does NOT silence the product.
                  Two channels, and the split already existed rather than being
                  built for this: the provider emails the PDF to whatever address
                  is here (its `customer.email` is single-valued, so there is no CC
                  to offer), while `_resolve_billing_recipient` resolves the admin
                  WORKER, so `subscription_invoice_issued` still reaches the person
                  who owns the subscription at the same moment. Both halves are
                  asserted in svdy-platform's
                  tests/subscription/test_dunning_copy_consistency.py, because a
                  future reader will reasonably assume one billing email means one
                  recipient and "simplify" it.

                  The first version of this copy said the invoice goes to the AP
                  address "instead", which under-sold the design: it told the admin
                  they would lose all visibility of their own billing.

                  Resolution order for the provider's address (asserted in
                  tests/organization/test_billing_identity.py): this field, then
                  the subscribing admin, then the org's public contact.

                  Always visible, and NOT inside the address-override block: who
                  receives the invoice is a different question from what the
                  invoice says, and the answer is "accounts payable" for a company
                  whose company address is perfectly correct. */}
              <Typography sx={{ ...styles.label, mt: 1.5 }}>
                {t('org:organizationProfile.billing.fields.email')}
              </Typography>
              <ProfileTextField
                {...fieldShared}
                name="billing_email"
                placeholder={t('org:organizationProfile.billing.fields.emailPlaceholder')}
                helperText={isEditing
                  ? t('org:organizationProfile.billing.fields.emailHelp')
                  : undefined}
                InputLabelProps={{ shrink: true }}
                label=""
              />
            </CardContent>
          </Card>
        </Grid>

      </Grid>

      {/* Sticky Save/Cancel bar */}
      {isEditing && (
        <StickyFormActions
  primary={(
    <Button
    variant="contained"
    size="small"
    startIcon={
    saving ? <CircularProgress size={16} color="inherit" /> : <Save />
    }
    onClick={handleSave}
    disabled={saving || timezoneMissing}
    sx={{
    borderRadius: 1,
    textTransform: 'none',
    fontSize: '0.875rem',
    }}
    >
    {saving ? t('org:organizationProfile.savingButton') : t('org:organizationProfile.saveButton')}
    </Button>
  )}
  secondary={(
    <Button
    variant="outlined"
    size="small"
    startIcon={<Cancel />}
    onClick={handleEditToggle}
    disabled={saving}
    sx={{
    borderRadius: 1,
    textTransform: 'none',
    fontSize: '0.875rem',
    color: 'text.secondary',
    borderColor: 'divider',
    }}
    >
    {t('org:organizationProfile.cancelButton')}
    </Button>
  )}
  note={(
    <Typography
    variant="caption"
    sx={{ color: 'warning.dark', ml: 'auto', fontWeight: 600 }}
    >
    {t('org:organizationProfile.stickyTimezone')}
    </Typography>
  )}
/>
      )}

      <Snackbar
        open={!!toast}
        autoHideDuration={3500}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity="success"
          onClose={() => setToast(null)}
          sx={{ width: '100%' }}
        >
          {toast}
        </Alert>
      </Snackbar>

      <LocationPickerDialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={handleLocationPick}
        title={t('org:organizationProfile.headquarters.pickerTitle')}
        initialAddress={{
          street_address: draft?.address ?? '',
          city: draft?.city ?? '',
          state: draft?.state ?? '',
          country: draft?.country ?? '',
          postal_code: draft?.postal_code ?? '',
        }}
      />

      <ConfirmDialog
        open={confirmingDiscard}
        tone="warning"
        title={t('org:organizationProfile.discardTitle')}
        description={t('org:organizationProfile.discardDescription')}
        confirmLabel={t('org:organizationProfile.discardConfirm')}
        onConfirm={handleConfirmDiscard}
        onClose={() => setConfirmingDiscard(false)}
      />
    </Box>
  );
};

export default OrganizationProfileComponent;
